/** @format */

import { memo } from 'react'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - DOM-свойства и атрибуты</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Свойства</h3>
      <p className={styles.description}>
        Во время построения DOM-дерева многие стандартные HTML-атрибуты становятся свойствами узлов. Посмотрим на
        несколько часто использующихся свойств.
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>hidden</code> - контролирует видимость элемента.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>value</code> - содержит текущий текстовый контент элементов input, select,
          textarea.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>checked</code> - хранит состояние чекбокса или радиокнопки.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>name</code> - хранит значение указанное в HTML-атрибуте name.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>src</code> - путь к изображению тега{' '}
          <code className={styles.code}>{`<img>`}</code>.
        </li>
      </ul>
      <iframe
        height="300"
        scrolling="no"
        title="module-08-props"
        src="https://codepen.io/goit-fe-adv/embed/RZmgpE?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.1 - Свойство Node.textContent</h4>
        <p className={styles.description}>
          <code className={styles.code}>elem.textContent</code> — свойство, содержит текстовый контент внутри элемента.
          Доступно для записи, при чем вне зависимости что будет передано в textContent, данные всегда будут записаны
          как текст.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="module-08-textContent"
          src="https://codepen.io/goit-fe-adv/embed/WEBjVV?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.2 - Свойство HTMLElement.style</h4>
        <p className={styles.description}>
          Используется для получения и установки инлайновых стилей. Возвращает объект{' '}
          <code className={styles.code}>CSSStyleDeclaration</code>, который содержит список всех свойств определенных
          только в инлайновом стиле элемента, а не весь CSS. Свойство можно как читать так и записывать. При записи,
          свойства записываются в <b>camelCase</b>, то есть <code className={styles.code}>background-color</code>{' '}
          превращается в <code className={styles.code}>element.style.backgroundColor</code> и т. д.{' '}
        </p>
        <CopyBlock
          text={`const button = document.querySelector('.btn');

button.style.backgroundColor = 'teal';
console.log(button.style); // объект инлайн стилей
`}
          language="html"
          showLineNumbers
          theme={shadesOfPurple}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.1.3 - Свойство Element.classList</h4>
        <p className={styles.description}>Объект, содержит методы для работы с классами элемента. </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>elem.classList.contains(cls)</code> - возвращает{' '}
            <code className={styles.code}>true</code> или <code className={styles.code}>false</code>, в зависимости от
            того, есть ли у элемента класс <code className={styles.code}>cls</code>
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>elem.classList.add(cls)</code> - добавляет класс{' '}
            <code className={styles.code}>cls</code> в список классов элемента
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>elem.classList.remove(cls)</code> - удаляет класс{' '}
            <code className={styles.code}>cls</code> из списка классов элемента
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>elem.classList.replace(oldClass, newClass)</code> - заменяет существующий
            класс на указанный
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>elem.classList.toggle(cls)</code> - если класса{' '}
            <code className={styles.code}>cls</code> нет, добавляет его, если есть, удаляет
          </li>
        </ul>
        <iframe
          height="300"
          scrolling="no"
          title="module-08-classList"
          src="https://codepen.io/goit-fe-adv/embed/eEaRME?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Атрибуты</h3>
      <p className={styles.description}>
        DOM-элементам соответствуют HTML-теги, у которых есть текстовые атрибуты. Доступ к атрибутам осуществляется при
        помощи стандартных методов. Эти методы работают со значением, которое находится в HTML.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>elem.hasAttribute(name)</code> - проверяет наличие аттрибута, возвращает{' '}
          <code className={styles.code}>true</code> или <code className={styles.code}>false</code>
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>elem.getAttribute(name)</code> - получает значение атрибута и возвращает его
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>elem.setAttribute(name, value)</code> - устанавливает атрибут
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>elem.removeAttribute(name)</code> - удаляет атрибут
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>elem.attributes</code> - свойство, возвращает коллекцию всех атрибутов элемента
        </li>
      </ul>
      <iframe
        height="300"
        scrolling="no"
        title="module-08-attributes"
        src="https://codepen.io/goit-fe-adv/embed/zdQzPP?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>3.2.1 - HTML5 data-атрибуты</h4>
        <p className={styles.description}>
          В HTML5 можно создавать произвольный атрибут и получать значения этого атрибута в JavaScript. Эту возможность
          используют для того чтобы упростить написание кода, например связать данные и разметку по уникальному
          идентификатору, указать тип действия кнопки и т. п.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="module-08-data-attributes"
          src="https://codepen.io/goit-fe-adv/embed/YOKWxQ?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.description}>
          <a
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.sitepoint.com/how-why-use-html5-custom-data-attributes/"
          >
            How You Can Use HTML5 Custom Data Attributes and Why
          </a>
        </p>
      </section>
    </section>
  </section>
))
